// Copyright (c) 2019 Comsearch, Inc., All rights reserved

import React from 'react'
import { success } from 'react-notification-system-redux'
import { change } from 'redux-form'
import { patchBulkPathEdit } from 'helpers/api'
import getBandOptionsFromPaths from 'redux/helpers/getBandOptionsFromPaths'
import getUpdatedBulkEditPathsDataByPathId from 'redux/modules/bulkEdit/thunks/getUpdatedBulkEditPathsDataByPathId'
import { formatFrequencyInfo } from 'redux/modules/pathDetails/thunks/handlePathDetailsSubmit/helpers'
import initializeBulkUpdateForm from 'redux/modules/bulkEdit/thunks/initializeBulkUpdateForm'

import {
  checkPathsWarningValidations,
  removeAllPathValidationsFromDrawer,
} from 'redux/modules/pathValidationsDrawer'

import {
  castToFloat,
  castToString,
} from 'helpers/reduxFormFunctions'

import {
  BULK_EDITS_FILTER,
  BULK_EDITS_FORM,
} from 'components/BulkUpdates/constants'

import {
  clearPathAndSiteHighlights,
  filterAndSortPaths,
  setBulkEditBandOptions, setBulkEditPaths,
} from 'redux/modules/bulkEdit'

// NOTE - any errors will be thrown to the parent to handle

const submitPathMode = (formValues, d, formProps) => async (dispatch, getState) => {
  const { updateStoredPathsAfterBulkEdit } = formProps

  const pathIds = getState().bulkEdit.get('highlightedPathIds')
  const visiblePathIds = getState().bulkEdit.get('visiblePathIds')
  const { initial } = getState().form[BULK_EDITS_FORM]

  // only send a band id if the user changed it,
  // otherwise send null
  const bandId = (
    initial.bandId !== formValues.bandId
      ? castToFloat(formValues.bandId)
      : null
  )

  // if all the visible paths are highlighted and the band id is not null
  const triggerFilterBandUpdate = (
    visiblePathIds.size === pathIds.size &&
    bandId !== null
  )

  const jobCode = formValues.jobCode || null
  const rcnNumber = formValues.rcnNumber || null

  const site1 = formatFrequencyInfo(formValues.frequencyPlans[0])
  const site2 = formatFrequencyInfo(formValues.frequencyPlans[1])

  const bulkUpdate = {
    bandId,
    jobCode,
    pathIds,
    rcnNumber,
    site1,
    site2,
  }

  const res = await patchBulkPathEdit(bulkUpdate)

  const updatedBulkEditPathsData = await dispatch(getUpdatedBulkEditPathsDataByPathId(pathIds))

  // update bulkEdit redux store with the path data
  dispatch(setBulkEditPaths(updatedBulkEditPathsData))
  // in case these changes have just removed paths from filtered list of visible paths,
  // we need to trigger an update to the visible paths.
  dispatch(filterAndSortPaths())

  // tell the original store of the path data that these paths have been updated.
  // the parent store can figure out what to do about that.
  // NOTE - we don't await this so the warnings can proceed in parallel.
  updateStoredPathsAfterBulkEdit(pathIds)

  // with the last submit, we may have changed what bands are used in this project
  // check to see if that's the case and update the band options accordingly.
  const { bandOptions } = dispatch(getBandOptionsFromPaths(updatedBulkEditPathsData))
  await dispatch(setBulkEditBandOptions(bandOptions))

  // so since we're on bulk edits, there are a bunch of different ways in which the user can
  // generate errors for multiple paths. As there is no clear requirement here,
  // the simplest solution for handling these errors is by clearing any previous errors
  await dispatch(removeAllPathValidationsFromDrawer())

  // if we've gotten here, then we'll check for warnings for these paths
  // the second argument is a callback for after the warnings check.
  await dispatch(checkPathsWarningValidations(pathIds, async () => {
    // clear all the highlighted sites in bulk edits
    await dispatch(clearPathAndSiteHighlights())

    // if all the visible paths just had their band changed, then the band filter
    // in BULK_EDITS_FILTER no longer has the previous band as an option.
    // update the filter so that it reflects the band that the user just assigned
    // to all those visible paths.
    if (triggerFilterBandUpdate) {
      await dispatch(change(BULK_EDITS_FILTER, 'bandId', castToString(bandId)))
    }

    await dispatch(initializeBulkUpdateForm())

    // filter and sort the displayed paths since we might have
    // updated some paths to the not currently selected band
    await dispatch(filterAndSortPaths())

    const idRoot = 'notification-successful-bulk-edit-save'

    // update UI to show user success message
    return dispatch(success({
      autoDismiss: 5,
      message: (
        <span id={`${idRoot}-message`}>
          {res.message}
        </span>
      ),
      uid: idRoot,
    }))
  }))
}

export default submitPathMode
